// Global Instructions Rule Applied!
//
// Orchestration for the Deal Pipeline → Budget tab: which funds the user
// follows there, and the budget read for them.
//
// The fund pick lives in the per-user app preference blob
// (PREF_APP_BUDGET_FUND_IDS) — absent until the user changes it, in which
// case the read resolves the cfg default funds and reports which ones, so
// the picker shows them without the frontend knowing the default. A change
// is dispatched to the store first and persisted fire-and-forget (the
// useSectionCollapse precedent).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { store, type RootState } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { showErrorToast, dismissErrorToast } from '../../../core/utils/errorToast.tsx'
import { setUserPreferencesAppData } from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import { persistAppPreference } from '../../global_configs/controller.ts'
import { PREF_APP_BUDGET_FUND_IDS, readBudgetFundIds } from '../../global_configs/appPreferences.ts'
import { fetchDealPipelineBudget } from '../services/dealsService.ts'
import type { DealPipelineBudget } from '../../../state_manager/data_models/deal_pipeline_budget.ts'

const BUDGET_LOAD_ERROR_KEY = 'deal-pipeline-budget-load'

export interface UseDealPipelineBudgetResult {
  budget: DealPipelineBudget | null
  loading: boolean
  /** The funds shown: the user's pick, else the funds the default resolved to. */
  selectedFundIds: string[]
  /** Commit + persist a new fund pick. An empty pick falls back to the default. */
  setSelectedFundIds: (fundIds: string[]) => void
  /** Re-read with the current pick; silent keeps the current figures on screen. */
  refresh: (silent?: boolean) => Promise<void>
}

export function useDealPipelineBudget(): UseDealPipelineBudgetResult {
  const storedFundIds = useSelector((state: RootState) => readBudgetFundIds(state.globalConfigs))
  const storedKey = storedFundIds ? storedFundIds.join(',') : ''

  const [budget, setBudget] = useState<DealPipelineBudget | null>(null)
  const [loading, setLoading] = useState(true)
  const requestRef = useRef(0)

  const load = useCallback(
    async (fundIds: string[] | null, silent: boolean) => {
      const requestId = ++requestRef.current
      if (!silent) setLoading(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const result = await fetchDealPipelineBudget(fundIds, token)
        if (requestId !== requestRef.current) return
        if (result.error || !result.data) {
          showErrorToast(result.error || 'Failed to load the pipeline budget', { key: BUDGET_LOAD_ERROR_KEY })
          return
        }
        dismissErrorToast(BUDGET_LOAD_ERROR_KEY)
        setBudget(result.data)
      } finally {
        if (requestId === requestRef.current && !silent) setLoading(false)
      }
    },
    []
  )

  // The stored pick drives the read: on mount, and whenever it changes.
  useEffect(() => {
    void load(storedFundIds, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, storedKey])

  const refresh = useCallback((silent = false) => load(storedFundIds, silent), [load, storedFundIds])

  const setSelectedFundIds = useCallback((fundIds: string[]) => {
    const next = { [PREF_APP_BUDGET_FUND_IDS]: fundIds.length > 0 ? fundIds : null }
    store.dispatch(setUserPreferencesAppData(next))
    void persistAppPreference(next).then((result) => {
      if (!result.success && process.env.NODE_ENV === 'development') {
        console.error('Failed to persist the Budget tab funds:', result.error)
      }
    })
  }, [])

  const selectedFundIds = useMemo(
    () => storedFundIds ?? (budget?.funds ?? []).map((f) => f.funds__id),
    [storedFundIds, budget]
  )

  return { budget, loading, selectedFundIds, setSelectedFundIds, refresh }
}
